import * as React from 'react';
import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { DynamicForm, dynamicSectionSurfaceSx, useResolvedSections, } from '../../../components/FormComponents/dynamicForm';
import { createInitialFormValues } from '../../../components/FormComponents/dynamicForm/form_validation';
import moment from 'moment';
import { buildFinanceAndDocumentsSections, CREATE_APPLICATION_STEP_ID, deriveFinanceValues, FINANCE_FIELD, FINANCE_SECTION_ID, } from '../config/create_application_schema';
import { fetchFinanceProductOptions, } from '../finance_lookup_service';
import { formatCurrencyDisplay, parseAmount } from '../config/form_field_factories';
import ApplicationInfoBanner from './ApplicationInfoBanner';
import ApplicationNotesPanel from './ApplicationNotesPanel';
import FilesUploadPanel from './FilesUploadPanel';
import { LOC_SUMMARY_TABLE_SX, TAKEDOWN_TABLE_BODY_ROW_SX, TAKEDOWN_TABLE_HEAD_ROW_SX, } from './ScheduleTakedownStyles';
import StepPanelHeader from './StepPanelHeader';
var LOC_SUMMARY_COLUMNS = [
    'Transaction',
    'Contact Category',
    'Total Approved',
    'Availability',
    'Credit Decision',
    'Expiration Date',
    'Status',
];
var LOC_CONTACT_CATEGORY = 'Line Of Credit';
var LOC_CREDIT_DECISION = 'Approved';
var LOC_DATE_FORMAT = 'MM/DD/YYYY | hh:mm A';
var NO_AMOUNT = '$0.00';
var NOT_PROVIDED = '--';
var LOC_LIMIT_MESSAGE = 'The amount financed is more than this line of credit has available.';
var FinanceAndDocumentsStep = React.forwardRef(function (_a, ref) {
    var totalAssetCost = _a.totalAssetCost, programAspireOId = _a.programAspireOId, uploadContext = _a.uploadContext, commentContext = _a.commentContext, getCustomerName = _a.getCustomerName, onDraftApplicationId = _a.onDraftApplicationId, isLineOfCredit = _a.isLineOfCredit, isConsumerProgram = _a.isConsumerProgram, _b = _a.locDetails, locDetails = _b === void 0 ? null : _b, statesList = _a.statesList, vendorConfig = _a.vendorConfig, isPrimaryCustomerVeteran = _a.isPrimaryCustomerVeteran;
    var formRef = React.useRef(null);
    var filesRef = React.useRef(null);
    var _c = React.useState([]), financeProducts = _c[0], setFinanceProducts = _c[1];
    var _d = React.useState({}), pricingOptionsByProduct = _d[0], setPricingOptionsByProduct = _d[1];
    var _e = React.useState(''), locLimitError = _e[0], setLocLimitError = _e[1];
    React.useEffect(function () {
        var isCancelled = false;
        fetchFinanceProductOptions(programAspireOId).then(function (products) {
            if (!isCancelled) {
                setFinanceProducts(products);
            }
        });
        return function () {
            isCancelled = true;
        };
    }, [programAspireOId]);
    var _f = React.useState(''), downPayment = _f[0], setDownPayment = _f[1];
    var _g = React.useState(''), debouncedDownPayment = _g[0], setDebouncedDownPayment = _g[1];
    React.useEffect(function () {
        var timeoutId = window.setTimeout(function () { return setDebouncedDownPayment(downPayment); }, 400);
        return function () { return window.clearTimeout(timeoutId); };
    }, [downPayment]);
    var handleFieldChange = React.useCallback(function (name, value) {
        if (name === FINANCE_FIELD.DownPayment) {
            setDownPayment(String(value !== null && value !== void 0 ? value : ''));
        }
    }, []);
    var schemaSections = React.useMemo(function () {
        return buildFinanceAndDocumentsSections(financeProducts, isLineOfCredit, isConsumerProgram, pricingOptionsByProduct, vendorConfig, isPrimaryCustomerVeteran);
    }, [
        financeProducts,
        isConsumerProgram,
        isLineOfCredit,
        isPrimaryCustomerVeteran,
        pricingOptionsByProduct,
        vendorConfig,
    ]);
    var sections = useResolvedSections(CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments, schemaSections);
    var initialValues = React.useMemo(function () { return createInitialFormValues(sections); }, [sections]);
    React.useEffect(function () {
        var _a;
        var _b;
        var formattedTotal = formatCurrencyDisplay(totalAssetCost);
        (_b = formRef.current) === null || _b === void 0 ? void 0 : _b.mergeValues((_a = {},
            _a[FINANCE_FIELD.TotalAssetCost] = formattedTotal,
            _a));
    }, [totalAssetCost]);
    var validateLocLimit = React.useCallback(function () {
        var _a, _b;
        if (!locDetails) {
            setLocLimitError('');
            return true;
        }
        var amountFinanced = parseAmount(((_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getValues()) !== null && _b !== void 0 ? _b : {})[FINANCE_FIELD.TotalAmountFinanced]);
        var isWithinLimit = amountFinanced <= locDetails.availableAmount;
        setLocLimitError(isWithinLimit ? '' : LOC_LIMIT_MESSAGE);
        return isWithinLimit;
    }, [locDetails]);
    React.useImperativeHandle(ref, function () { return ({
        validate: function () {
            var _a, _b, _c, _d;
            var areFilesValid = (_b = (_a = filesRef.current) === null || _a === void 0 ? void 0 : _a.validate()) !== null && _b !== void 0 ? _b : true;
            var isFormValid = (_d = (_c = formRef.current) === null || _c === void 0 ? void 0 : _c.validate()) !== null && _d !== void 0 ? _d : true;
            return areFilesValid && validateLocLimit() && isFormValid;
        },
        getPayload: function () {
            var _a, _b;
            return ({
                financeAndDocuments: (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getValues()) !== null && _b !== void 0 ? _b : {},
                financeProducts: financeProducts,
            });
        },
        getFiles: function () { var _a, _b; return (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFiles()) !== null && _b !== void 0 ? _b : {}; },
        reset: function () { var _a; return (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.reset(); },
        getPreview: function () {
            var _a, _b, _c, _d;
            return ({
                label: 'Files Upload, Finance Options & Comments',
                records: [{ sections: sections, values: (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getValues()) !== null && _b !== void 0 ? _b : {} }],
                documents: (_d = (_c = filesRef.current) === null || _c === void 0 ? void 0 : _c.getDocuments()) !== null && _d !== void 0 ? _d : [],
            });
        },
    }); }, [financeProducts, sections, validateLocLimit]);
    var sectionHeaders = React.useMemo(function () {
        var _a;
        return (_a = {},
            _a[FINANCE_SECTION_ID.FinanceOptions] = (React.createElement(StepPanelHeader, { isSubHeading: true, icon: React.createElement(RequestQuoteOutlinedIcon, null), title: isLineOfCredit ? 'Line of Credit' : 'Finance Options' })),
            _a);
    }, [isLineOfCredit]);
    var formatAmount = function (amount) {
        return Number.isFinite(amount) && amount !== 0 ? formatCurrencyDisplay(amount) : NO_AMOUNT;
    };
    var locExpiry = (locDetails === null || locDetails === void 0 ? void 0 : locDetails.expirationDate)
        ? moment(locDetails.expirationDate).utcOffset(0)
        : null;
    return (React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { item: true, xs: 12, lg: 8.5 },
            React.createElement(Box, { sx: { marginBottom: '20px' } },
                React.createElement(StepPanelHeader, { isSubHeading: true, icon: React.createElement(DescriptionOutlinedIcon, null), title: "Files Upload" }),
                React.createElement(Box, { sx: dynamicSectionSurfaceSx(true) },
                    React.createElement(ApplicationInfoBanner, { icon: React.createElement(LightbulbOutlinedIcon, null), title: "Quick Tip:", message: "You can upload any of these documents: Driving Licence, Bank Statement, or Financial Statement." }),
                    React.createElement(FilesUploadPanel, { ref: filesRef, uploadContext: uploadContext, onDraftApplicationId: onDraftApplicationId }))),
            React.createElement(DynamicForm, { ref: formRef, sections: sections, initialValues: initialValues, deriveValues: deriveFinanceValues, sectionHeaders: sectionHeaders, onFieldChange: handleFieldChange, spacing: 2.5 }),
            locDetails && (React.createElement(Box, { mt: "15px" },
                React.createElement(TableContainer, { component: Paper, sx: LOC_SUMMARY_TABLE_SX },
                    React.createElement(Table, { "aria-label": "Line of credit summary" },
                        React.createElement(TableHead, null,
                            React.createElement(TableRow, { sx: TAKEDOWN_TABLE_HEAD_ROW_SX }, LOC_SUMMARY_COLUMNS.map(function (column) { return (React.createElement(TableCell, { key: column, align: "center" }, column)); }))),
                        React.createElement(TableBody, null,
                            React.createElement(TableRow, { sx: TAKEDOWN_TABLE_BODY_ROW_SX },
                                React.createElement(TableCell, { align: "center" }, locDetails.transactionNum || NOT_PROVIDED),
                                React.createElement(TableCell, { align: "center" }, LOC_CONTACT_CATEGORY),
                                React.createElement(TableCell, { align: "center" }, formatAmount(locDetails.approvedAmount)),
                                React.createElement(TableCell, { align: "center" }, formatAmount(locDetails.availableAmount)),
                                React.createElement(TableCell, { align: "center" }, LOC_CREDIT_DECISION),
                                React.createElement(TableCell, { align: "center" }, (locExpiry === null || locExpiry === void 0 ? void 0 : locExpiry.isValid())
                                    ? locExpiry.format(LOC_DATE_FORMAT)
                                    : NOT_PROVIDED),
                                React.createElement(TableCell, { align: "center" }, locDetails.status || NOT_PROVIDED))))),
                locLimitError && (React.createElement(Typography, { sx: { pt: 1, fontSize: '13px', color: '#D84315' }, role: "alert" }, locLimitError))))),
        React.createElement(Grid, { item: true, xs: 12, lg: 3.5 },
            React.createElement(Box, { sx: { position: { lg: 'sticky' }, top: { lg: 12 } } },
                React.createElement(ApplicationNotesPanel, { commentContext: commentContext, getCustomerName: getCustomerName, onDraftApplicationId: onDraftApplicationId })))));
});
FinanceAndDocumentsStep.displayName = 'FinanceAndDocumentsStep';
export default FinanceAndDocumentsStep;
